import React, { useCallback, useEffect, useRef, useState } from "react";
import AccountManagement from "./AccountManagement";
import ChatBot from "./ChatBot";

const MIN_CHAT_WIDTH = 300;
const MAX_CHAT_RATIO = 0.85;
const DEFAULT_CHAT_RATIO = 0.6;

const Conversations = ({
  companyName = "",
  contacts = [],
  interactions = [],
  summary = null,
  sentiment = null,
  bant = null,
  isLoading = false,
  isSummaryLoading = false,
  connectMeetings = null,
}) => {
  const containerRef = useRef(null);
  // Kept as a ratio, not pixels: the panel has to give width back when the app
  // sidebar expands instead of squeezing the left pane alone.
  const [chatRatio, setChatRatio] = useState(DEFAULT_CHAT_RATIO);
  const [isResizing, setIsResizing] = useState(false);

  const startResizing = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, []);

  const stopResizing = useCallback(() => {
    setIsResizing(false);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  const resize = useCallback(
    (e) => {
      if (!isResizing || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newWidth = Math.max(
        MIN_CHAT_WIDTH,
        Math.min(rect.right - e.clientX, rect.width * MAX_CHAT_RATIO),
      );
      setChatRatio(newWidth / rect.width);
    },
    [isResizing],
  );

  useEffect(() => {
    if (!isResizing) return;
    window.addEventListener("mousemove", resize);
    window.addEventListener("mouseup", stopResizing);
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResizing);
    };
  }, [isResizing, resize, stopResizing]);

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 w-full overflow-hidden gap-3"
    >
      <AccountManagement
        className="h-full min-h-0 min-w-0 flex-1"
        companyName={companyName}
        contacts={contacts}
        interactions={interactions}
        summary={summary}
        sentiment={sentiment}
        bant={bant}
        isLoading={isLoading}
        isSummaryLoading={isSummaryLoading}
        connectMeetings={connectMeetings}
      />
      <div
        className="relative h-full shrink-0"
        style={{ width: `${chatRatio * 100}%` }}
      >
        <div
          onMouseDown={startResizing}
          className={`absolute left-0 top-0 bottom-0 z-10 w-1.5 -translate-x-1/2 cursor-col-resize transition-colors hover:bg-secondary/40 ${isResizing ? "bg-secondary/50" : ""
            }`}
          style={{ userSelect: "none" }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize chat panel"
        />
        <ChatBot companyName={companyName} className="h-full min-h-0 w-full" />
      </div>
    </div>
  );
};

export default Conversations;
